import {
    SixButton,
    SixDropdown,
    SixIconButton,
    SixInput,
    SixMenu,
    SixMenuItem,
    SixSelect,
    SixSwitch
} from "@six-group/ui-library-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ConflationPeriods, TabNames } from "../Assets/variables";
import { startMarketDataStreamMessage, stopStreamMessageV3 } from "../Assets/apiDefinitionExamples";
import { v3WebSocketUrlToUse } from "../Utils/environmentHelpers";
import { websocketAuthenticationUrl } from "../Utils/websocketAuthentication";
import {
    buildStopCommand,
    editorReason,
    payloadReason,
    readCommand,
    stopTargetReason,
    streamStateChange,
    CONFLATION_TYPES,
    INTERVAL_CONFLATION_TYPE,
    NO_STREAM_RUNNING_MESSAGE,
    QUALITY_OF_SERVICE_TYPES,
    QUOTE_TYPES,
    SCHEMES,
    START_COMMAND_KEY,
    STOP_COMMAND_KEY
} from "../Utils/websocketCommandValidation";
import CertificationAlert from "./CertificationAlert";

/* The endpoint the "json2" sub-protocol is served on, resolved from the portal origin. */
const DEFAULT_WEBSOCKET_URL = v3WebSocketUrlToUse();
const WEBSOCKET_SUB_PROTOCOL = "json2";

const HEARTBEAT_MS = 10_000;

/* The console keeps the last N entries instead of growing without bound. Streams can push
   several messages per second, so an unbounded log eventually freezes the tab. */
const MAX_CONSOLE_ENTRIES = 1_000;

/* The field row starts out agreeing with the example payload that is already in the editor,
   rather than empty, so the form and the JSON never contradict each other before anything has
   been touched. Read from the example itself so the two cannot drift apart. */
const START_DEFAULTS = readCommand(startMarketDataStreamMessage).command?.[START_COMMAND_KEY] ?? {};

const ConnectionStates = {
    Disconnected: "disconnected",
    Connecting: "connecting",
    Connected: "connected"
};

const ConnectionStateLabels = {
    [ConnectionStates.Disconnected]: "Disconnected",
    [ConnectionStates.Connecting]: "Connecting…",
    [ConnectionStates.Connected]: "Connected"
};

const EntryKinds = {
    Sent: "sent",
    Received: "received",
    System: "system",
    Error: "error"
};

const APIWebsocketExplorer = () => {
    const { setActiveTabItem } = useOutletContext();

    const stopCommandElementRef = useRef(null);
    const keepAliveElementRef = useRef(null);
    const consoleElementRef = useRef(null);

    const webSocketRef = useRef(null);
    const authenticationControllerRef = useRef(null);
    const heartbeatTimerRef = useRef(null);
    const entryIdRef = useRef(0);

    /* The start command sent for each stream, keyed by streamId, so a chip can restore the whole
       request - editor, field row, Fields selection - not just the stop command. A ref because
       nothing renders from it. A rejected start never becomes a chip, so its entry is
       unreachable. */
    const sentStartCommandsRef = useRef({});

    /* Held in state rather than read off the element: the React wrapper around the Six web
       components reassigns every prop it was given on each re-render, without dirty checking
       (see @lit/react's create-component), so a constant `value` would overwrite whatever was
       typed the next time anything here re-rendered - a console line is enough. */
    const [webSocketUrl, setWebSocketUrl] = useState(DEFAULT_WEBSOCKET_URL);

    const [connectionState, setConnectionState] = useState(ConnectionStates.Disconnected);
    const [entries, setEntries] = useState([]);
    const [receivedCount, setReceivedCount] = useState(0);
    const [commandText, setCommandText] = useState(startMarketDataStreamMessage);
    const [stopCommandText, setStopCommandText] = useState(stopStreamMessageV3);

    /* The start command's own fields, held in state for the same reason as the URL above. An
       empty one means "leave whatever is in the payload alone" - see updateQuery. */
    const [scheme, setScheme] = useState(START_DEFAULTS.scheme ?? "");
    const [listingId, setListingId] = useState(START_DEFAULTS.id ?? "");
    const [streamId, setStreamId] = useState(START_DEFAULTS.streamId ?? "");
    const [qualityOfService, setQualityOfService] = useState(START_DEFAULTS.qualityOfService ?? "");
    const [conflationType, setConflationType] = useState(START_DEFAULTS.conflationType ?? "");
    const [conflationPeriod, setConflationPeriod] = useState(START_DEFAULTS.conflationPeriod ?? "");

    /* Seeded from the example payload like the controls above. Read off the element and starting
       empty meant the row claimed no fields while the payload asked for three, and updateQuery
       could not tell "not touched yet" from "cleared on purpose" (SFLROCKET-3436). */
    const [quoteTypes, setQuoteTypes] = useState(
        Array.isArray(START_DEFAULTS.fields) ? START_DEFAULTS.fields : []
    );

    /* Which streams are live on the current connection. The API has no operation that
       modifies a running stream - the only client operations are startMarketDataStream and
       stop - so a start for an id that is already streaming is rejected outright, and the
       changed quote types never reach the server. Knowing what is live lets the start be
       preceded by a stop so the re-subscription actually takes effect. */
    const [activeStreamIds, setActiveStreamIds] = useState([]);

    const isConnected = connectionState === ConnectionStates.Connected;
    const isBusy = connectionState !== ConnectionStates.Disconnected;

    /* Validated as it is typed rather than on send, so an invalid payload never reaches the
       API and the reason is on screen while it is still being edited. */
    const commandProblem = useMemo(() => editorReason(commandText), [commandText]);
    const stopCommandProblem = useMemo(
        () => stopTargetReason(stopCommandText, activeStreamIds),
        [stopCommandText, activeStreamIds]
    );

    const canSendCommand = isConnected && commandProblem === null;
    const canSendStopCommand = isConnected && stopCommandProblem === null;

    /* The period is only meaningful for interval conflation, so it is offered only then -
       every other selection would make it a value the API rejects outright. */
    const showsConflationPeriod = conflationType === INTERVAL_CONFLATION_TYPE;

    /* Which chip reads as selected is derived from the stop command rather than tracked
       separately, so the two cannot disagree - typing an id by hand highlights its chip. */
    const selectedStreamId = useMemo(
        () => readCommand(stopCommandText).command?.[STOP_COMMAND_KEY]?.streamId ?? null,
        [stopCommandText]
    );

    useEffect(() => {
        setActiveTabItem(TabNames.WebsocketExplorer);
    }, []);

    useEffect(() => {
        return () => {
            stopHeartbeat();
            closeSocket();
        };
    }, []);

    /* Keep the newest line in view, the same way a terminal does. */
    useEffect(() => {
        const consoleElement = consoleElementRef.current;
        if (consoleElement) {
            consoleElement.scrollTop = consoleElement.scrollHeight;
        }
    }, [entries]);

    /* When the stream on screen stops - chip x, stop command, or the server dropping it - fall
       back to the newest survivor, the same "newest wins" rule a fresh start follows. Driven off
       activeStreamIds because all three paths end there, and an effect reads current state
       instead of whatever a handler closed over. Nothing to do once the last stream is gone. */
    useEffect(() => {
        if (activeStreamIds.length === 0) return;
        if (selectedStreamId && activeStreamIds.includes(selectedStreamId)) return;

        showStream(activeStreamIds[activeStreamIds.length - 1]);
    }, [activeStreamIds, selectedStreamId]);

    function log(kind, text) {
        entryIdRef.current += 1;
        const entry = { id: entryIdRef.current, kind, text, time: new Date().toLocaleTimeString() };

        setEntries((previousEntries) => {
            const nextEntries = [...previousEntries, entry];
            return nextEntries.length > MAX_CONSOLE_ENTRIES
                ? nextEntries.slice(nextEntries.length - MAX_CONSOLE_ENTRIES)
                : nextEntries;
        });
    }

    function closeSocket() {
        authenticationControllerRef.current?.abort();
        authenticationControllerRef.current = null;

        const webSocket = webSocketRef.current;
        if (!webSocket) return;

        /* Detach the handlers before closing: close() completes asynchronously and by the time
           onclose fires the component may already be unmounted, which would make the handler
           write to state that no longer exists. */
        webSocket.onopen = null;
        webSocket.onmessage = null;
        webSocket.onclose = null;
        webSocket.onerror = null;
        webSocket.close();
        webSocketRef.current = null;

        /* Streams live and die with the connection they were started on. */
        setActiveStreamIds([]);
        sentStartCommandsRef.current = {};
    }

    function isSocketOpen() {
        return webSocketRef.current?.readyState === WebSocket.OPEN;
    }

    function sendPing() {
        if (!isSocketOpen()) return;

        const ping = { type: "ping", ts: Date.now() };
        webSocketRef.current.send(JSON.stringify(ping));
        log(EntryKinds.Sent, `[PING] ${JSON.stringify(ping)}`);
    }

    function startHeartbeatIfEnabled() {
        stopHeartbeat();
        if (!keepAliveElementRef.current?.checked) return;
        if (!isSocketOpen()) return;

        heartbeatTimerRef.current = setInterval(() => {
            if (!isSocketOpen()) {
                stopHeartbeat();
                return;
            }
            sendPing();
        }, HEARTBEAT_MS);
    }

    function stopHeartbeat() {
        if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
        }
    }

    function handleKeepAliveChange() {
        if (!isSocketOpen()) return;

        if (keepAliveElementRef.current?.checked) {
            startHeartbeatIfEnabled();
        } else {
            stopHeartbeat();
        }
    }

    /* six-input-input carries an empty payload, so the value comes off the element. Guarded
       on the type so a payload-less event can never blank the field. */
    function handleUrlInput(event) {
        const value = event.target?.value;
        if (typeof value === "string") {
            setWebSocketUrl(value);
        }
    }

    /* Same guard as the URL field, for the same reason: a payload-less six-input-input event
       must not blank the field it fires on. */
    function handleFieldInput(event, setValue) {
        const value = event.target?.value;
        if (typeof value === "string") {
            setValue(value);
        }
    }

    /* six-select carries the new selection on the event detail. Single selects only, so an
       array would mean the wrong component and is treated as no selection. */
    function handleSelectChange(event, setValue) {
        const value = event.detail?.value;
        setValue(typeof value === "string" ? value : "");
    }

    /* Fills the text input rather than replacing it, so typed and picked are one piece of state.
       sixMenuItemSelected carries the item's value as detail.name. */
    function handleConflationPeriodSelected(event) {
        const value = event.detail?.name;
        if (typeof value === "string") {
            setConflationPeriod(value);
        }
    }

    /* The only multiple select, so the selection arrives as an array and an empty one is a real
       state - cleared - not "nothing happened". The dropdown's own Select all / Deselect all
       button reports through here too, so both arrive as an ordinary selection. */
    function handleQuoteTypesChange(event) {
        const value = event.detail?.value;

        setQuoteTypes(Array.isArray(value) ? value : [value].filter(Boolean));
    }

    async function connect() {
        if (authenticationControllerRef.current || webSocketRef.current) return;
        const webSocketEndpoint = webSocketUrl.trim();

        if (!webSocketEndpoint) {
            log(EntryKinds.Error, "Enter a WebSocket URL before connecting.");
            return;
        }

        closeSocket();
        setConnectionState(ConnectionStates.Connecting);
        log(EntryKinds.System, `Connecting to ${webSocketEndpoint} …`);

        /* Browsers do ont offer certificate pick on wss connection, so we need to make a HTTP request */
        const authenticationUrl = websocketAuthenticationUrl(webSocketEndpoint);
        if (authenticationUrl) {
            const controller = new AbortController();
            authenticationControllerRef.current = controller;
            try {
                await fetch(authenticationUrl, {
                    method: "GET",
                    credentials: "include",
                    cache: "no-store",
                    redirect: "error",
                    signal: controller.signal
                });
            } catch {
                // Route failures must not prevent the actual handshake.
            }
            if (controller.signal.aborted) return;
            authenticationControllerRef.current = null;
        }

        let webSocket;
        try {
            webSocket = new WebSocket(webSocketEndpoint, WEBSOCKET_SUB_PROTOCOL);
        } catch (error) {
            setConnectionState(ConnectionStates.Disconnected);
            log(EntryKinds.Error, `Could not open the connection: ${error.message}`);
            return;
        }

        webSocketRef.current = webSocket;

        webSocket.onopen = () => {
            setConnectionState(ConnectionStates.Connected);
            log(EntryKinds.System, "Connection opened.");
            startHeartbeatIfEnabled();
        };

        webSocket.onmessage = (message) => {
            let text;
            let frame = null;
            try {
                frame = JSON.parse(message.data);
                text = JSON.stringify(frame);
            } catch (error) {
                text = message.data;
            }

            trackStreamState(frame);
            setReceivedCount((previousCount) => previousCount + 1);
            log(EntryKinds.Received, text);
        };

        webSocket.onclose = (event) => {
            /* A native CloseEvent has no enumerable own properties, so JSON.stringify(event)
               always collapses to "{}" - pull the fields out explicitly so a rejection
               (e.g. a missing client certificate) is actually visible. */
            stopHeartbeat();
            webSocketRef.current = null;
            setActiveStreamIds([]);
            sentStartCommandsRef.current = {};
            setConnectionState(ConnectionStates.Disconnected);
            log(EntryKinds.System, `Connection closed: code=${event.code} reason="${event.reason || "(none)"}" wasClean=${event.wasClean}`);
        };

        webSocket.onerror = () => {
            log(EntryKinds.Error, "Connection error. Check the URL, client certificate, and gateway route. The browser does not expose the handshake failure reason here.");
        };
    }

    function disconnect() {
        stopHeartbeat();
        closeSocket();
        setConnectionState(ConnectionStates.Disconnected);
    }

    /* Only the server's own frames say what is streaming. Tracking sends instead would leave a
       rejected start behind as a phantom stream, and the next start would then be preceded by a
       stop for something that never existed. Rejections are Error frames carrying the type they
       reject, which streamStateChange filters out. */
    function trackStreamState(frame) {
        const change = streamStateChange(frame);
        if (!change) return;

        if (change.running) {
            setActiveStreamIds((previousIds) =>
                previousIds.includes(change.streamId) ? previousIds : [...previousIds, change.streamId]);

            /* The whole view follows whatever actually started, not just the stop command:
               moving one and leaving the others behind is what let the highlighted chip
               describe a different stream than the form and the editor. */
            showStream(change.streamId);
        } else {
            setActiveStreamIds((previousIds) => previousIds.filter((id) => id !== change.streamId));
            delete sentStartCommandsRef.current[change.streamId];
        }
    }

    function sendMessage(command) {
        const payload = JSON.stringify(command);
        webSocketRef.current.send(payload);
        log(EntryKinds.Sent, payload);
    }

    /* Re-subscribing means stopping and starting again: the stop is sent explicitly rather
       than silently, so the console still shows the exchange the API actually saw. */
    function restartRunningStream(command) {
        const streamId = command?.[START_COMMAND_KEY]?.streamId;
        if (!streamId || !activeStreamIds.includes(streamId)) return;

        log(EntryKinds.System, `Stream "${streamId}" is already running - stopping it before restarting it with the current query.`);
        setActiveStreamIds((previousIds) => previousIds.filter((id) => id !== streamId));
        sendMessage({ [STOP_COMMAND_KEY]: { streamId } });
    }

    /* The buttons are already disabled while a command is unsendable; these checks are what
       makes that true for the keyboard shortcut too, and for anything added later. */
    function send(rawCommand) {
        if (!isSocketOpen()) {
            log(EntryKinds.Error, "Not connected. Use Connect first.");
            return;
        }

        const { command, reason } = readCommand(rawCommand);
        if (reason) {
            log(EntryKinds.Error, reason);
            return;
        }

        const payloadProblem = payloadReason(command);
        if (payloadProblem) {
            log(EntryKinds.Error, payloadProblem);
            return;
        }

        restartRunningStream(command);
        rememberStartCommand(command);
        sendMessage(command);
    }

    /* Recorded at send time because this is the only point that knows what was asked for -
       the server's own START frame carries just the streamId back. A restart overwrites the
       previous entry, so a chip always reflects the request the stream is running under. */
    function rememberStartCommand(command) {
        const start = command?.[START_COMMAND_KEY];
        if (start?.streamId) {
            sentStartCommandsRef.current[start.streamId] = start;
        }
    }

    function runCommand() {
        send(commandText);
    }

    function stopCommand() {
        send(stopCommandText);
    }

    /* Points the whole page at one stream: stop payload, the start command it runs under, and
       every form control. All of it moves together or the page lies - the chip highlight comes
       from the stop command, so moving that alone left the highlight on one stream while the
       editor and field row described another, and Update Query then mixed the two. Unsent editor
       changes are replaced, which is the point: a half-typed command is that same mismatch. */
    function showStream(streamId) {
        setStopCommandText(buildStopCommand(streamId));

        const start = sentStartCommandsRef.current[streamId];
        if (!start) return;

        setCommandText(JSON.stringify({ [START_COMMAND_KEY]: start }, null, 2));

        setStreamId(start.streamId ?? "");
        setScheme(start.scheme ?? "");
        setListingId(start.id ?? "");
        setQualityOfService(start.qualityOfService ?? "");
        setConflationType(start.conflationType ?? "");
        setConflationPeriod(start.conflationPeriod ?? "");
        setQuoteTypes(Array.isArray(start.fields) ? start.fields : []);
    }

    /* Selecting a chip shows that stream. Stopping it stays an explicit act, either the stop
       button below or the x on the chip itself. */
    function selectStream(streamId) {
        showStream(streamId);
        stopCommandElementRef.current?.focus();
    }

    /* Stops one specific stream, so the only streams offered are ones that exist. */
    function stopStream(streamId) {
        if (!isSocketOpen()) {
            log(EntryKinds.Error, "Not connected. Use Connect first.");
            return;
        }

        setActiveStreamIds((previousIds) => previousIds.filter((id) => id !== streamId));

        /* Only the payload for the stream just stopped is cleared - anything else sitting in
           the editor is the user's own and is left alone. */
        if (streamId === selectedStreamId) {
            setStopCommandText("");
        }

        sendMessage({ [STOP_COMMAND_KEY]: { streamId } });
    }

    /* "Update Query" folds the field row into the start command already in the editor, so the
       form and the raw payload never disagree. Only filled-in text controls are written: a blank
       one means "leave the payload alone", not "clear it". The Fields select is the exception and
       is always written - seeded from the payload, so empty can only mean the user cleared it. */
    function updateQuery() {
        const selectedQuoteTypes = quoteTypes;

        const filledFields = Object.entries({
            streamId: streamId.trim(),
            scheme: scheme,
            id: listingId.trim(),
            qualityOfService: qualityOfService,
            conflationType: conflationType
        }).filter(([, value]) => value !== "");

        if (filledFields.length === 0 && selectedQuoteTypes.length === 0) {
            log(EntryKinds.Error, "Fill in at least one field, or select a value, before updating the query.");
            return;
        }

        const { command, reason } = readCommand(commandText);
        if (reason) {
            log(EntryKinds.Error, reason);
            return;
        }

        const start = command[START_COMMAND_KEY];
        if (!start || typeof start !== "object" || Array.isArray(start)) {
            log(EntryKinds.Error, `The query can only be applied to a "${START_COMMAND_KEY}" command.`);
            return;
        }

        const updatedFields = filledFields.map(([field]) => field);
        filledFields.forEach(([field, value]) => { start[field] = value; });

        /* The period belongs to interval conflation alone, so selecting any other type takes it
           back out of the payload instead of leaving behind a value the API would reject. */
        if (showsConflationPeriod) {
            if (conflationPeriod !== "") {
                start.conflationPeriod = conflationPeriod;
                updatedFields.push("conflationPeriod");
            }
        } else if (conflationType !== "" && start.conflationPeriod !== undefined) {
            delete start.conflationPeriod;
            updatedFields.push("conflationPeriod (removed)");
        }

        /* Written unconditionally: the select mirrors the payload, so an empty selection means
           cleared. Leaving it out kept deselected fields in the query (SFLROCKET-3436), and []
           now fails the "at least one field" check, which is the message the user should get. */
        start.fields = selectedQuoteTypes;
        updatedFields.push(`fields (${selectedQuoteTypes.length})`);

        setCommandText(JSON.stringify(command, null, 2));
        log(EntryKinds.System, `Query updated: ${updatedFields.join(", ")}.`);
    }

    function clearConsole() {
        setEntries([]);
        setReceivedCount(0);
    }

    function handleCommandKeyDown(event) {
        if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            if (!canSendCommand) return;
            runCommand();
        }
    }

    return (
        <div id="tab-websocket-explorer" className="api-panel">
            <CertificationAlert />

            <section className="websocket-explorer__connection">
                <SixInput
                    className="websocket-explorer__url"
                    label="WebSocket URL"
                    placeholder="wss://host/web/v3/websocket"
                    value={webSocketUrl}
                    onSixInputInput={handleUrlInput}
                    autocomplete="url">
                </SixInput>
                <SixButton type="action" disabled={isBusy} onClick={() => connect()}>Connect</SixButton>
                <SixButton type="action-outline" disabled={!isBusy} onClick={() => disconnect()}>Disconnect</SixButton>
                <span className={`websocket-explorer__status websocket-explorer__status--${connectionState}`}>
                    {ConnectionStateLabels[connectionState]}
                </span>
            </section>

            <p id="websocket-authentication-help">
                When you click Connect, your browser may request a client certificate for the entered endpoint.
                Select your installed certificate if prompted, the WebSocket connection will then be attempted.
                Certificate selection is managed by your browser and may be cached.
            </p>

            <section className="websocket-explorer__keep-alive">
                <SixSwitch ref={keepAliveElementRef} onSixSwitchChange={() => handleKeepAliveChange()}>
                    Keep alive (ping every {HEARTBEAT_MS / 1000}s)
                </SixSwitch>
                <SixButton type="link" disabled={!isConnected} onClick={() => sendPing()}>Send ping now</SixButton>
            </section>

            <section className="websocket-explorer__query" aria-label="Update query">
                <div className="websocket-explorer__fields">
                    <SixSelect
                        className="websocket-explorer__field"
                        filter
                        label="Scheme"
                        placeholder="Choose scheme"
                        filterPlaceholder="Search schemes"
                        value={scheme}
                        onSixSelectChange={(event) => handleSelectChange(event, setScheme)}>
                        { SCHEMES.map((schemeValue) => (
                            <SixMenuItem key={schemeValue} value={schemeValue}>{schemeValue}</SixMenuItem>
                        )) }
                    </SixSelect>
                    <SixInput
                        className="websocket-explorer__field"
                        label="ID"
                        placeholder="1222171_4"
                        value={listingId}
                        onSixInputInput={(event) => handleFieldInput(event, setListingId)}>
                    </SixInput>
                    <SixInput
                        className="websocket-explorer__field"
                        label="Stream ID"
                        placeholder="MarketDataStreamId-001"
                        value={streamId}
                        onSixInputInput={(event) => handleFieldInput(event, setStreamId)}>
                    </SixInput>
                    <SixSelect
                        className="websocket-explorer__field"
                        label="Quality of service"
                        placeholder="Choose quality of service"
                        value={qualityOfService}
                        onSixSelectChange={(event) => handleSelectChange(event, setQualityOfService)}>
                        { QUALITY_OF_SERVICE_TYPES.map((qualityOfServiceValue) => (
                            <SixMenuItem key={qualityOfServiceValue} value={qualityOfServiceValue}>{qualityOfServiceValue}</SixMenuItem>
                        )) }
                    </SixSelect>
                    <SixSelect
                        className="websocket-explorer__field"
                        label="Conflation type"
                        placeholder="Choose conflation type"
                        value={conflationType}
                        onSixSelectChange={(event) => handleSelectChange(event, setConflationType)}>
                        { CONFLATION_TYPES.map((conflationTypeValue) => (
                            <SixMenuItem key={conflationTypeValue} value={conflationTypeValue}>{conflationTypeValue}</SixMenuItem>
                        )) }
                    </SixSelect>
                    { showsConflationPeriod &&
                        /* Text input, not a select: the spec puts no enum on conflationPeriod,
                           only "Min 100ms, max 1 minute", so eight presets were narrower than
                           the API allows. They stay as a dropdown in the input's suffix slot. */
                        <SixInput
                            className="websocket-explorer__field"
                            label="Conflation period"
                            placeholder="PT2S"
                            value={conflationPeriod}
                            onSixInputInput={(event) => handleFieldInput(event, setConflationPeriod)}>
                            <SixDropdown slot="suffix" placement="bottom-end" hoist>
                                <SixIconButton
                                    slot="trigger"
                                    name="expand_more"
                                    label="Choose a conflation period" />
                                <SixMenu onSixMenuItemSelected={handleConflationPeriodSelected}>
                                    { ConflationPeriods.map((period) => (
                                        <SixMenuItem key={period.value} value={period.value}>{period.label}</SixMenuItem>
                                    )) }
                                </SixMenu>
                            </SixDropdown>
                        </SixInput> }
                </div>
                <div className="websocket-explorer__query-row">
                    <SixSelect
                        className="websocket-explorer__quote-types"
                        multiple
                        filter
                        clearable
                        selectAllButton
                        label="Fields"
                        placeholder="Choose fields"
                        filterPlaceholder="Search fields"
                        value={quoteTypes}
                        onSixSelectChange={handleQuoteTypesChange}>
                        { QUOTE_TYPES.map((quoteType) => (
                            <SixMenuItem key={quoteType} value={quoteType}>{quoteType}</SixMenuItem>
                        )) }
                    </SixSelect>
                    <SixButton
                        type="action"
                        className="websocket-explorer__query-action"
                        onClick={() => updateQuery()}>Update Query</SixButton>
                </div>
            </section>

            <section className="websocket-explorer__command" aria-label="Command">
                <div className="websocket-explorer__editor">
                    <textarea
                        className="websocket-explorer__editor-input"
                        rows={14}
                        spellCheck="false"
                        aria-label="Command"
                        placeholder="# Type your command"
                        value={commandText}
                        onChange={(event) => setCommandText(event.target.value)}
                        onKeyDown={handleCommandKeyDown}>
                    </textarea>
                    <button
                        type="button"
                        className="websocket-explorer__icon-button websocket-explorer__icon-button--play"
                        title="Send command (Ctrl + Enter)"
                        aria-label="Send command"
                        disabled={!canSendCommand}
                        onClick={() => runCommand()}>
                        <span className="material-icons">play_arrow</span>
                    </button>
                </div>
                { commandProblem && <p className="websocket-explorer__validation">{commandProblem}</p> }
            </section>

            <section className="websocket-explorer__command websocket-explorer__command--stop" aria-label="Stop command">
                <div className="websocket-explorer__streams">
                    { activeStreamIds.length === 0
                        ? <p className="websocket-explorer__streams-empty">{NO_STREAM_RUNNING_MESSAGE}</p>
                        : activeStreamIds.map((streamId) => (
                            <span
                                key={streamId}
                                className={`websocket-explorer__stream-chip${streamId === selectedStreamId ? " websocket-explorer__stream-chip--selected" : ""}`}>
                                <button
                                    type="button"
                                    className="websocket-explorer__stream-chip-select"
                                    title={`Load the request and stop command for ${streamId}`}
                                    aria-label={`Load the request and stop command for stream ${streamId}`}
                                    aria-pressed={streamId === selectedStreamId}
                                    onClick={() => selectStream(streamId)}>
                                    {streamId}
                                </button>
                                <button
                                    type="button"
                                    className="websocket-explorer__stream-chip-stop"
                                    title={`Stop stream ${streamId}`}
                                    aria-label={`Stop stream ${streamId}`}
                                    onClick={() => stopStream(streamId)}>
                                    <span className="material-icons" aria-hidden="true">close</span>
                                </button>
                            </span>
                        )) }
                </div>
                <div className="websocket-explorer__editor">
                    <textarea
                        ref={stopCommandElementRef}
                        className="websocket-explorer__editor-input"
                        rows={5}
                        spellCheck="false"
                        aria-label="Stop command"
                        placeholder="# Stop command"
                        value={stopCommandText}
                        onChange={(event) => setStopCommandText(event.target.value)}>
                    </textarea>
                    <button
                        type="button"
                        className="websocket-explorer__icon-button websocket-explorer__icon-button--stop"
                        title="Send stop command"
                        aria-label="Send stop command"
                        disabled={!canSendStopCommand}
                        onClick={() => stopCommand()}>
                        <span className="material-icons">stop</span>
                    </button>
                </div>
                { stopCommandProblem && stopCommandProblem !== NO_STREAM_RUNNING_MESSAGE &&
                    <p className="websocket-explorer__validation">{stopCommandProblem}</p> }
            </section>

            <section className="websocket-explorer__output" aria-label="Console">
                <div className="websocket-explorer__section-toolbar websocket-explorer__section-toolbar--end">
                    <SixButton type="link" onClick={() => clearConsole()}>Clear console</SixButton>
                </div>
                <div className="websocket-explorer__console" ref={consoleElementRef} role="log" aria-live="polite">
                    { entries.length === 0
                        ? <p className="websocket-explorer__console-placeholder">Messages exchanged with the API appear here.</p>
                        : entries.map((entry) => (
                            <p key={entry.id} className={`websocket-explorer__console-line websocket-explorer__console-line--${entry.kind}`}>
                                <span className="websocket-explorer__console-time">{entry.time}</span>
                                <span className="websocket-explorer__console-text">{entry.text}</span>
                            </p>
                        )) }
                </div>
                <div className="websocket-explorer__console-footer">
                    Received messages: <b>{receivedCount}</b>
                </div>
            </section>
        </div>
    );
};

export default APIWebsocketExplorer;
